import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';
import { keyBy, pick } from 'lodash';
import { createCachedSelector } from 're-reselect';

import slice from './canvases.slice';
import canvasesAdapter from './canvases.adapter';
import utils from './canvases.utils';

import products from 'domains/products';
import optionGroups from 'domains/option_groups';

import * as boundsUtils from 'utils/bounds_util';

import { validDimension } from 'utils/type_guards';

import type { RootState } from 'store/wos_store';
import type { CartProductForCreate } from 'domains/cart_products';
import type { CanvasConfiguration } from '.';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const defaultSelectors = canvasesAdapter.getSelectors();

const selectAllCanvasProductIds = createSelector(
  [compose(defaultSelectors.selectAll, localState)],
  (allCanvases) => {
    return allCanvases.map((c) => c.product);
  }
);

const selectCanvasesLocked = (state: LocalState) => state.activeLock;
const selectArrangementPosition = (state: LocalState) => state.arrangementPosition;

const selectCanvasForCreateCartProduct = (
  state: RootState,
  canvas: CanvasConfiguration
): CartProductForCreate => {
  const { product: product_api_id, rotation } = canvas;
  if (!product_api_id) throw new Error('No Product Selected');

  const product = state.products.entities[product_api_id];
  if (!product) throw new Error('No Product Found');

  let custom_size;
  if (products.utils.isCustomSizeProduct(product)) {
    custom_size = pick(canvas, ['height', 'width']);
    if (!validDimension(custom_size)) throw new Error('Invalid Custom Size Dimensions');
  }

  return {
    product_api_id,
    canvas_rotation: rotation,
    custom_size,
    border_color: canvas.borderColor,
    quantity: 1, // Will be overwritten in cart_product_creator. frankly this whole selector should probably be in that module
  };
};

/**
 * re-reselect selector will create multple caches based on the key passed
 * in the final call, called the keySelector -- since this selector
 * will be called for each canvas component instance, we want to make
 * sure to cache per-instance, otherise it will always re-calculate
 */
const selectNodesForCanvas = createCachedSelector(
  products.selectors.selectEntities,
  optionGroups.selectors.selectEntities,
  (state: RootState, canvasId: string) => defaultSelectors.selectById(localState(state), canvasId),
  (productsById, optionGroupsById, canvas) => {
    if (!canvas) return [];

    const nodes = utils.buildCanvasProductNodes(productsById, canvas.product);
    const overrides = utils.buildCanvasOverrideNodes(optionGroupsById, canvas.options);
    const keyedOverrides = keyBy(overrides, 'uid');

    return nodes.map((n) => ({
      ...n,
      ...keyedOverrides[n.uid],
    }));
  }
)((_state, canvasId) => canvasId);

const selectCanvasNodeCalculatedBounds = (
  state: RootState,
  { canvasId, nodeUid }: { canvasId: string; nodeUid: string }
) => {
  const canvas = defaultSelectors.selectById(localState(state), canvasId);
  const node = selectNodesForCanvas(state, canvasId).find((n) => n.uid === nodeUid);
  if (!canvas || !canvas.product || !node) return [0, 0, 0, 0];

  const product = products.selectors.selectById(state, canvas.product);
  if (!product) return [0, 0, 0, 0];

  // For now, this is not useful for text nodes
  if (node.node_type === 3) throw new Error('Only for use in image & mask nodes');

  const nodeType = node.node_type === 1 ? 'image' : 'mask';

  return node.bounds
    .split(',')
    .map((bound) => boundsUtils.calculateBoundDimension(bound, nodeType, product, canvas));
};

export default {
  ...canvasesAdapter.getSelectors(localState),
  locked: compose(selectCanvasesLocked, localState),
  arrangementPosition: compose(selectArrangementPosition, localState),
  // Already globalized
  forCreateCartProduct: selectCanvasForCreateCartProduct,
  allProductIds: selectAllCanvasProductIds,
  nodesForCanvas: selectNodesForCanvas,
  calculatedBoundsForCanvasNode: selectCanvasNodeCalculatedBounds,
};
